import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, TextInput, View } from 'react-native';
import RenderHTML, { defaultSystemFonts } from 'react-native-render-html';

import { IconCloseSvg } from '../../../assets/inventory';
import { sendContactMessage } from '../../../services/api';
import { analyticsEvents } from '../../../services/firebase/analytics-events.service';
import { goldGlow } from '../../../shared/glowStyles';
import { playSound } from '../../../utils/sound';
import {
	ACHIEVEMENTS_FONT as FONT,
	achievementsColors,
	achievementsGlow,
	FILTER_ACTIVE_FILL,
	FILTER_FILL,
	GOLD_BUTTON_FILL,
	PANEL_WIDTH,
	RADIUS_MD,
} from '../Achievements/achievementsTheme';
import { GradientFill } from '../common/GradientFill';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import RedesignModal from './RedesignModal';
import { createPopupHtmlStyles } from './RedesignPopup';
import { useRedesignScale } from './useRedesignScale';

/**
 * Help & Contacts, opened from the side menu and from the Settings popup's "Contacts" row. It is
 * drawn as a sibling of "Modal / Settings" (5054:78827): the same 358-wide dark maroon panel
 * floating over the screen, the centred gold title with the close glyph, and inside it the
 * achievements' filter pills as the Help / Contacts tabs, the edit-profile field plates for the
 * form and the gold modal action for Send. Every number is in the 390-wide design space and is
 * scaled once.
 */
const PANEL = { height: 545, radius: 10, paddingX: 12, paddingY: 14, gap: 12 };
/** Same darkened backdrop as the Settings popup, so the two read as one family. */
const PANEL_TINT = 'rgba(26,6,3,0.82)';
const HEADER = { height: 40, button: 32, closeIcon: 24 };
const TABS = { height: 34, gap: 6 };
const FIELD = { input: 40, message: 112, radius: 6 };
const BUTTON = { height: 47, radius: 10 };
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 1000;
const EMAIL_PATTERN =
	/^[a-zA-Z0-9]+([._-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9]+([-.]?[a-zA-Z0-9]+)*(\.[a-zA-Z0-9]{2,})+$/;

const colors = {
	gold50: achievementsColors.gold50,
	gold200: achievementsColors.gold200,
	primary: achievementsColors.primary,
	secondary: achievementsColors.secondary,
	muted: achievementsColors.muted,
	/** surface/raised - the form's plate and the inputs. */
	raised: achievementsColors.surface,
	/** The plate's orange hairline. */
	plateBorder: achievementsColors.panelOrange,
	/** border/subtle - an input at rest. */
	inputBorder: '#5C2416',
	/** border/gold - the focused input. */
	inputFocus: '#E89200',
	error: '#FF8B77',
	onGold: achievementsColors.onGold,
	goldRim: achievementsColors.goldStroke,
} as const;

type Tab = 'help' | 'contacts';
type Field = 'name' | 'email' | 'message';

type HelpModalProps = {
	onClose: () => void;
	onSubmit?: (payload: { name: string; email: string; message: string }) => void;
};

const HelpModal: React.FC<HelpModalProps> = ({ onClose, onSubmit }) => {
	const { t } = useTranslation();
	const scale = useRedesignScale({ fitHeight: PANEL.height });
	const styles = useMemo(() => createStyles(scale), [scale]);

	const [activeTab, setActiveTab] = useState<Tab>('help');
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [message, setMessage] = useState('');
	const [submitted, setSubmitted] = useState(false);
	const [sending, setSending] = useState(false);
	const [sendStatus, setSendStatus] = useState<'idle' | 'sent' | 'failed'>('idle');
	const [focused, setFocused] = useState<Field | null>(null);
	const sendRef = useRef<View>(null);

	useEffect(() => {
		analyticsEvents.trackOpenHelp();
	}, []);

	const nameError = useMemo(() => {
		if (!submitted) return '';
		if (!name.trim())
			return t('T_SITE_CONTACTS_HELP_ERROR_NAME_REQUIRED', {
				defaultValue: 'Please enter your name.',
			});
		return '';
	}, [submitted, name, t]);

	const emailError = useMemo(() => {
		if (!submitted) return '';
		if (!email.trim())
			return t('T_SITE_CONTACTS_HELP_ERROR_EMAIL_REQUIRED', {
				defaultValue: 'Please enter your email.',
			});
		if (!EMAIL_PATTERN.test(email))
			return t('T_SITE_CONTACTS_HELP_ERROR_EMAIL_PATTERN', {
				defaultValue: 'Please enter a valid email.',
			});
		return '';
	}, [submitted, email, t]);

	const messageError = useMemo(() => {
		if (!submitted) return '';
		const trimmed = message.trim();
		if (!trimmed)
			return t('T_SITE_CONTACTS_HELP_ERROR_MESSAGE_REQUIRED', {
				defaultValue: 'Please enter a message.',
			});
		if (trimmed.length < MESSAGE_MIN || trimmed.length > MESSAGE_MAX) {
			return t('T_SITE_CONTACTS_HELP_ERROR_MESSAGE_MINLENGTH', {
				defaultValue: 'Message must be 10 to 1000 characters.',
			});
		}
		return '';
	}, [submitted, message, t]);

	const handleSend = async () => {
		setSubmitted(true);
		setSendStatus('idle');
		// The errors are memoised on `submitted`, so the first press validates against the fields directly.
		const valid =
			!!name.trim() &&
			EMAIL_PATTERN.test(email) &&
			message.trim().length >= MESSAGE_MIN &&
			message.trim().length <= MESSAGE_MAX;
		if (!valid || sending) return;

		const payload = {
			name: name.trim(),
			email: email.trim(),
			message: message.trim(),
		};

		if (onSubmit) {
			onSubmit(payload);
			return;
		}

		setSending(true);
		try {
			await sendContactMessage(payload);
			// v1 modal.help.component: Notification once the contact form is accepted.
			playSound('Notification');
			setMessage('');
			setSubmitted(false);
			setSendStatus('sent');
		} catch (error) {
			console.warn('Contact form failed:', error);
			setSendStatus('failed');
		} finally {
			setSending(false);
		}
	};

	// An untranslated key comes back as itself; that is no help to show anyone.
	const helpKey = 'T_SITE_CONTACTS_HELP_HELP_CONTENT';
	const html = t(helpKey);
	const hasHelp = !!html && html !== helpKey;
	const htmlWidth = (PANEL_WIDTH - 2 * PANEL.paddingX) * scale;
	const tagsStyles = useMemo(() => createPopupHtmlStyles(scale), [scale]);
	const systemFonts = useMemo(() => [...defaultSystemFonts, FONT], []);

	const tabs: Array<{ key: Tab; label: string }> = [
		{ key: 'help', label: t('T_SITE_CONTACTS_HELP_HELP', { defaultValue: 'Help' }) },
		{ key: 'contacts', label: t('T_SITE_CONTACTS_HELP_CONTACTS', { defaultValue: 'Contacts' }) },
	];

	const inputProps = (field: Field) => ({
		onFocus: () => setFocused(field),
		onBlur: () => setFocused(current => (current === field ? null : current)),
	});

	const inputStyle = (field: Field, error: string) => [
		styles.input,
		Platform.OS === 'web' && styles.webInputReset,
		focused === field && styles.inputFocused,
		!!error && styles.inputError,
	];

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				tint: PANEL_TINT,
				overScreen: true,
			}}
		>
			<View style={styles.body}>
				<View style={styles.header}>
					<View style={styles.headerButton} />
					<Text style={styles.title} numberOfLines={1}>
						{t('T_SITE_MENU_CONTACTS', { defaultValue: 'Help & Contacts' })}
					</Text>
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_CLOSE_HELP', { defaultValue: 'Close help' })}
						onPress={onClose}
						hitSlop={8}
						style={styles.headerButton}
					>
						<IconCloseSvg width={HEADER.closeIcon * scale} height={HEADER.closeIcon * scale} />
					</Pressable>
				</View>

				<View style={styles.tabs} accessibilityRole='tablist'>
					{tabs.map(tab => {
						const isActive = tab.key === activeTab;
						return (
							<Pressable
								key={tab.key}
								accessibilityRole='tab'
								accessibilityState={{ selected: isActive }}
								onPress={() => setActiveTab(tab.key)}
								style={[styles.tab, isActive ? styles.tabActive : styles.tabRest]}
							>
								<GradientFill stops={isActive ? FILTER_ACTIVE_FILL : FILTER_FILL} />
								<Text
									numberOfLines={1}
									style={[styles.tabLabel, isActive ? styles.tabLabelActive : styles.tabLabelRest]}
								>
									{tab.label}
								</Text>
							</Pressable>
						);
					})}
				</View>

				{activeTab === 'help' ? (
					<RedesignScrollView
						key='help'
						contentContainerStyle={styles.helpContent}
						indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
						reserveGutter={false}
					>
						{hasHelp ? (
							<RenderHTML
								contentWidth={htmlWidth}
								source={{ html }}
								systemFonts={systemFonts}
								tagsStyles={tagsStyles}
								baseStyle={styles.htmlBase}
							/>
						) : null}
					</RedesignScrollView>
				) : (
					<RedesignScrollView
						key='contacts'
						contentContainerStyle={styles.formContent}
						avoidKeyboard={{ submitRef: sendRef }}
						indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
					>
						<View style={styles.plate}>
							<View style={styles.field}>
								<Text style={styles.label}>
									{t('T_SITE_CONTACTS_HELP_NAME', { defaultValue: 'Name' })}
								</Text>
								<TextInput
									{...inputProps('name')}
									style={inputStyle('name', nameError)}
									value={name}
									onChangeText={setName}
									autoCapitalize='words'
									autoCorrect={false}
									returnKeyType='next'
								/>
								{!!nameError && <Text style={styles.errorText}>{nameError}</Text>}
							</View>

							<View style={styles.field}>
								<Text style={styles.label}>
									{t('T_SITE_CONTACTS_HELP_EMAIL', { defaultValue: 'Email' })}
								</Text>
								<TextInput
									{...inputProps('email')}
									style={inputStyle('email', emailError)}
									value={email}
									onChangeText={setEmail}
									keyboardType='email-address'
									autoCapitalize='none'
									autoCorrect={false}
									returnKeyType='next'
								/>
								{!!emailError && <Text style={styles.errorText}>{emailError}</Text>}
							</View>

							<View style={styles.field}>
								<View style={styles.labelRow}>
									<Text style={styles.label}>
										{t('T_SITE_CONTACTS_HELP_MESSAGE', { defaultValue: 'Message' })}
									</Text>
									<Text
										style={[styles.counter, message.length >= MESSAGE_MAX && styles.counterLimit]}
									>
										{message.length}/{MESSAGE_MAX}
									</Text>
								</View>
								<TextInput
									{...inputProps('message')}
									style={[...inputStyle('message', messageError), styles.messageInput]}
									value={message}
									onChangeText={text => text.length <= MESSAGE_MAX && setMessage(text)}
									multiline
									textAlignVertical='top'
								/>
								{!!messageError && <Text style={styles.errorText}>{messageError}</Text>}
							</View>
						</View>

						<Pressable
							ref={sendRef}
							accessibilityRole='button'
							onPress={handleSend}
							disabled={sending}
							accessibilityState={{ disabled: sending }}
							style={({ pressed }) => [styles.send, (pressed || sending) && styles.sendPressed]}
						>
							<GradientFill stops={GOLD_BUTTON_FILL} radius={(BUTTON.radius - 1) * scale} />
							<Text style={styles.sendLabel}>
								{t('T_SITE_CONTACTS_HELP_SEND', { defaultValue: 'Send' })}
							</Text>
						</Pressable>
						{sendStatus === 'sent' && (
							<Text style={styles.sentText}>
								{t('T_SITE_CONTACTS_HELP_SUCCESS', {
									defaultValue: 'Your message was sent successfully.',
								}).trim()}
							</Text>
						)}
						{sendStatus === 'failed' && (
							<Text style={[styles.errorText, styles.statusText]}>
								{t('T_SITE_CONTACTS_HELP_ERROR', {
									defaultValue:
										'Something went wrong. The message was not sent. Please, try again.',
								})}
							</Text>
						)}
					</RedesignScrollView>
				)}
			</View>
		</RedesignModal>
	);
};

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	const hairline = Math.max(1, scale);
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingHorizontal: s(PANEL.paddingX),
			paddingVertical: s(PANEL.paddingY),
			gap: s(PANEL.gap),
		},
		header: {
			height: s(HEADER.height),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		headerButton: {
			width: s(HEADER.button),
			height: s(HEADER.button),
			alignItems: 'center',
			justifyContent: 'center',
		},
		title: {
			flex: 1,
			textAlign: 'center',
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(24),
			lineHeight: s(24 * 1.3),
			color: colors.gold50,
		},
		tabs: {
			flexDirection: 'row',
			height: s(TABS.height),
			gap: s(TABS.gap),
		},
		tab: {
			flex: 1,
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(8),
			borderRadius: s(RADIUS_MD),
			borderWidth: hairline,
			overflow: 'hidden',
		},
		tabRest: {
			borderColor: achievementsColors.border,
		},
		tabActive: {
			borderColor: achievementsColors.borderActive,
			...achievementsGlow.filterActive,
		},
		// DS/Label/M, a step up so a two-tab row does not look sparse.
		tabLabel: {
			fontFamily: FONT,
			fontWeight: '700',
			fontSize: s(14),
			lineHeight: s(14 * 1.2),
			letterSpacing: s(0.3),
			textTransform: 'uppercase',
			textAlign: 'center',
		},
		tabLabelRest: {
			color: colors.secondary,
		},
		tabLabelActive: {
			color: colors.primary,
		},
		helpContent: {
			paddingBottom: s(4),
		},
		htmlBase: {
			fontFamily: FONT,
			color: colors.secondary,
			fontSize: s(14),
			lineHeight: s(20),
		},
		formContent: {
			gap: s(12),
			paddingBottom: s(4),
		},
		plate: {
			gap: s(12),
			padding: s(12),
			borderRadius: s(12),
			borderWidth: hairline,
			borderColor: colors.plateBorder,
			backgroundColor: colors.raised,
		},
		field: {
			gap: s(6),
		},
		labelRow: {
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		label: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(12),
			lineHeight: s(14.4),
			color: colors.secondary,
		},
		counter: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(11),
			lineHeight: s(14),
			color: colors.muted,
		},
		counterLimit: {
			color: colors.error,
		},
		input: {
			height: s(FIELD.input),
			paddingHorizontal: s(10),
			paddingVertical: 0,
			borderRadius: s(FIELD.radius),
			borderWidth: hairline,
			borderColor: colors.inputBorder,
			backgroundColor: achievementsColors.well,
			color: colors.gold50,
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(16),
		},
		inputFocused: {
			borderColor: colors.inputFocus,
		},
		// The gold border is the focus ring; the browser's own outline would double it.
		webInputReset: { outlineStyle: 'none', resize: 'none' } as any,
		inputError: {
			borderColor: colors.error,
		},
		messageInput: {
			height: s(FIELD.message),
			paddingTop: s(10),
			paddingBottom: s(10),
			lineHeight: s(20),
		},
		errorText: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(12),
			lineHeight: s(14),
			color: colors.error,
		},
		statusText: {
			textAlign: 'center',
		},
		sentText: {
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(13),
			lineHeight: s(16),
			textAlign: 'center',
			color: colors.gold200,
		},
		send: {
			height: s(BUTTON.height),
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: s(BUTTON.radius),
			borderWidth: hairline,
			borderColor: colors.goldRim,
			...goldGlow,
		},
		sendPressed: {
			opacity: 0.85,
		},
		sendLabel: {
			fontFamily: FONT,
			fontWeight: '900',
			fontSize: s(18),
			letterSpacing: s(0.36),
			textTransform: 'uppercase',
			color: colors.onGold,
		},
	});
};

export default HelpModal;
